import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type { IWorkspaceWebhook, IWorkspaceWebhookLog } from "@/types/workspace";

export class WorkspaceWebhooksStore {
  webhooks: IWorkspaceWebhook[] = [];
  currentWebhook: IWorkspaceWebhook | null = null;
  logs: IWorkspaceWebhookLog[] = [];
  isLoadingList = false;
  isLoadingDetail = false;
  isLoadingLogs = false;
  activeWebhookId: string | null = null;
  lastGeneratedSecret: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  async fetchWebhooks(slug: string): Promise<IWorkspaceWebhook[]> {
    this.isLoadingList = true;
    try {
      const webhooks = await this.rootStore.workspaceWebhookService.listWebhooks(slug);
      runInAction(() => {
        this.webhooks = webhooks;
      });
      return webhooks;
    } finally {
      runInAction(() => {
        this.isLoadingList = false;
      });
    }
  }

  async fetchWebhook(slug: string, webhookId: string): Promise<IWorkspaceWebhook> {
    this.isLoadingDetail = true;
    try {
      const webhook = await this.rootStore.workspaceWebhookService.getWebhook(slug, webhookId);
      runInAction(() => {
        this.currentWebhook = webhook;
        this.webhooks = this.webhooks.some((item) => item.id === webhook.id)
          ? this.webhooks.map((item) => (item.id === webhook.id ? webhook : item))
          : [...this.webhooks, webhook];
      });
      return webhook;
    } finally {
      runInAction(() => {
        this.isLoadingDetail = false;
      });
    }
  }

  async createWebhook(
    slug: string,
    data: Partial<IWorkspaceWebhook>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceWebhook> {
    this.activeWebhookId = "creating";
    try {
      const response = await this.rootStore.workspaceWebhookService.createWebhook(slug, data);
      const { secret_key: secret = null, ...webhook } = response;
      runInAction(() => {
        this.lastGeneratedSecret = secret;
        this.webhooks = [...this.webhooks, webhook as IWorkspaceWebhook];
      });
      track("webhook_created", { webhook_id: webhook.id, source });
      return webhook as IWorkspaceWebhook;
    } finally {
      runInAction(() => {
        this.activeWebhookId = null;
      });
    }
  }

  async updateWebhook(
    slug: string,
    webhookId: string,
    data: Partial<IWorkspaceWebhook>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceWebhook> {
    this.activeWebhookId = webhookId;
    // Read before the write: `fields` is what this save changed (NG-2064).
    const fields = changedFields(
      data,
      this.webhooks.find((item) => item.id === webhookId) ??
        (this.currentWebhook?.id === webhookId ? this.currentWebhook : undefined),
    );
    try {
      const webhook = await this.rootStore.workspaceWebhookService.updateWebhook(
        slug,
        webhookId,
        data,
      );
      runInAction(() => {
        this.currentWebhook = webhook;
        this.webhooks = this.webhooks.map((item) => (item.id === webhook.id ? webhook : item));
      });
      track("webhook_updated", { webhook_id: webhookId, fields, source });
      return webhook;
    } finally {
      runInAction(() => {
        this.activeWebhookId = null;
      });
    }
  }

  async deleteWebhook(
    slug: string,
    webhookId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeWebhookId = webhookId;
    try {
      await this.rootStore.workspaceWebhookService.deleteWebhook(slug, webhookId);
      runInAction(() => {
        this.webhooks = this.webhooks.filter((item) => item.id !== webhookId);
        if (this.currentWebhook?.id === webhookId) {
          this.currentWebhook = null;
        }
      });
      track("webhook_deleted", { webhook_id: webhookId, source });
    } finally {
      runInAction(() => {
        this.activeWebhookId = null;
      });
    }
  }

  async regenerateSecret(
    slug: string,
    webhookId: string,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceWebhook> {
    this.activeWebhookId = webhookId;
    try {
      const response = await this.rootStore.workspaceWebhookService.regenerateSecret(
        slug,
        webhookId,
      );
      const { secret_key: secret = null, ...webhook } = response;
      runInAction(() => {
        this.lastGeneratedSecret = secret;
        this.currentWebhook = webhook as IWorkspaceWebhook;
        this.webhooks = this.webhooks.map((item) =>
          item.id === webhook.id ? (webhook as IWorkspaceWebhook) : item,
        );
      });
      track("webhook_secret_regenerated", { webhook_id: webhookId, source });
      return webhook as IWorkspaceWebhook;
    } finally {
      runInAction(() => {
        this.activeWebhookId = null;
      });
    }
  }

  async fetchLogs(slug: string, webhookId: string): Promise<IWorkspaceWebhookLog[]> {
    this.isLoadingLogs = true;
    try {
      const logs = await this.rootStore.workspaceWebhookService.listLogs(slug, webhookId);
      runInAction(() => {
        this.logs = logs;
      });
      return logs;
    } finally {
      runInAction(() => {
        this.isLoadingLogs = false;
      });
    }
  }

  clearLastGeneratedSecret(): void {
    this.lastGeneratedSecret = null;
  }
}
